import { useTranslation } from 'next-i18next'
import { ThunderboltOutlined, AimOutlined } from '@ant-design/icons'
import { useRouter } from 'next/router'

import BadgeCard from '~/statistics-v2/components/BadgeCard'
import ResponsiveImage from '~/components/Image/ResponsiveImage'
import StatisticProgressBar from '~/statistics-v2/components/StatsProgressBar'
import { getOverallGrade } from '~/statistics-v2/utils/statistics.utils'
import { TopicStats } from '~/statistics-v2/types/statistics.types'
import { TopicId } from '~/dashboard/utils/learningPath.utils'
import { AppLanguage } from '~/types/locale'

export default function TopicCard({
  topic,
  hideMustKnows,
  onTopicSelect,
}: {
  topic: TopicStats
  hideMustKnows: boolean
  onTopicSelect: (topicStats: TopicStats) => void
}): React.ReactElement {
  const { t } = useTranslation('statisticsV2')
  const progress = (topic.completedSubtopics / topic.totalSubtopics) * 100
  const router = useRouter()
  const accuracy = (topic.correctQuestions / topic.totalQuestions) * 100 || 0
  const grade = getOverallGrade(accuracy, router.locale as AppLanguage)
  const totalActivities = (topic.totalQuestionsAttempted || 0) + (topic.totalMustKnowQuestionsAttempted || 0)

  const showAccuracy = accuracy > 0
  const showActivities = totalActivities > 0

  return (
    <div className="flex flex-col items-end w-full h-full min-h-70 bg-gradient-blue-purple border border-white/10 rounded-4 px-4 py-3 gap-2 justify-between">
      <div className="flex flex-col lg:flex-row justify-center items-start lg:items-center w-full gap-4 ">
        <div className={`flex justify-start gap-2 lg:items-center ${!topic.isCompleted ? 'grayscale' : ''}`}>
          <ResponsiveImage
            alt="Certificate"
            fit="contain"
            height={1080}
            imageClassName="w-7.5 lg:w-17"
            srcKey={`curriculum/certificate/mastery-certificate-${topic.topicSlug ?? TopicId.NUMBERS}_v4.png`}
            width={1080}
          />
          <h3 className="text-white text-5 font-bold lg:hidden">{topic.name}</h3>
        </div>
        <div className="flex flex-col gap-2 h-full flex-1 w-full justify-center">
          <h3 className="text-white text-2xl font-black hidden lg:flex">{topic.name}</h3>
          {showAccuracy && (
            <BadgeCard
              isSlim
              srcKey="icons/products/test_prep_badge.png"
              subTitle={topic.name ?? ''}
              title={t('currently')}
              value={grade}
            />
          )}
        </div>
      </div>

      <div className=" border w-full border-white/20 rounded-4 p-4 flex flex-col gap-1 h-15">
        <StatisticProgressBar
          barHeight="regular"
          barType="straight"
          FirstSubtitle={`${topic.completedSubtopics} / ${topic.totalSubtopics} ${t('subTopics')}`}
          progressPercentage={progress}
          SecondSubtitle={
            hideMustKnows ? undefined : `🧩 ${topic.completedMustKnows} / ${topic.totalMustKnows} ${t('mustKnows')}`
          }
        />
      </div>

      <div className="flex w-full justify-between items-end">
        <div className="flex w-full gap-1 lg:gap-3">
          {showAccuracy && (
            <div className="flex bg-black/30 rounded px-2 lg:px-4 gap-1 py-1 lg:py-2 items-center border border-white/5 h-9 w-full lg:w-auto">
              <AimOutlined className="text-green-lime text-xs" />
              <span className="text-blue-pastel text-xs">
                {t('accuracy')} {Math.round(accuracy)}%
              </span>
            </div>
          )}
          {showActivities && (
            <div className="flex bg-black/30 rounded px-1 lg:px-4 gap-1 py-1 lg:py-2 items-center border border-white/5 h-9 w-full lg:w-auto">
              <ThunderboltOutlined className="text-green-lime text-xs" />
              <span className="text-blue-pastel text-xs ">
                {t('activities')} {totalActivities}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="flex w-full h-9 min-h-9 justify-end">
        <button
          className="bg-transparent w-22 min-h-0h-9 hover:bg-white/5 text-white text-xs rounded border-2 border-white/60 transition-all"
          onClick={() => onTopicSelect?.(topic)}
        >
          {t('seeMore')}
        </button>
      </div>
    </div>
  )
}
